import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import { WeeklyCalendar } from './WeeklyCalendar';
import LogoLoader from './LogoLoader';

import CheckMarkIcon from '../Images/check-mark-icon.svg';

// Key to track if we've already processed a setMeeting operation
const PROCESS_KEY = 'setMeeting_processed';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

// ============================================
// SHARED STYLED COMPONENTS
// ============================================

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  color: #192947;
`;

const LoadingMessage = styled.div`
  font-size: 20px;
  text-align: center;
  max-width: 400px;
  
  p {
    margin: 10px 0;
  }
  
  .small-text {
    font-size: 16px;
    opacity: 0.7;
    margin-top: 10px;
  }
`;

// ============================================
// "WHAT'S NEXT?" MODAL STYLED COMPONENTS
// ============================================

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(23, 36, 62, 0.49);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 720px;
  background: linear-gradient(160deg, #ffffff 0%, #f9fbff 50%, #f5f8fd 100%);
  border: 1px solid rgba(100, 150, 255, 0.12);
  border-radius: 24px;
  padding: 48px 40px;
  box-shadow: 
    0 24px 80px rgba(39, 68, 131, 0.14),
    0 0 0 1px rgba(100, 150, 255, 0.06),
    inset 0 1px 0 rgba(39, 68, 131, 0.02);
  animation: ${slideUp} 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(100, 150, 255, 0.3), transparent);
  }

  @media (max-width: 768px) {
    padding: 32px 24px;
    border-radius: 20px;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const ModalExpertHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #edf1f9;

  @media (max-width: 768px) {
    gap: 12px;
    margin-bottom: 24px;
    padding-bottom: 16px;
  }
`;

const ExpertAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid rgba(100, 150, 255, 0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ExpertInfo = styled.div`
  flex: 1;
`;

const ExpertNameText = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #192947;
  margin: 0 0 4px 0;
`;

const ExpertRoleText = styled.p`
  font-size: 14px;
  color: #7b8aa3;
  margin: 0;
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #192947;
  text-align: center;
  margin: 0 0 8px 0;
  letter-spacing: -0.5px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ModalSubtitle = styled.p`
  font-size: 15px;
  color: #7b8aa3;
  text-align: center;
  margin: 0 0 36px 0;
  line-height: 1.5;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 28px;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const OptionCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 22px 24px;
  background: #f8faff;
  border: 1px solid #edf1f9;
  border-radius: 16px;
  cursor: pointer;
  text-align: left;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent, rgba(100, 150, 255, 0.02));
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    background: #f5f8fd;
    border-color: rgba(100, 150, 255, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(39, 68, 131, 0.053);

    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 16px 16px;
    gap: 12px;
    border-radius: 14px;
  }
`;

const OptionIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 22px;
  background: ${props => props.$bg || 'rgba(45, 91, 237, 0.15)'};
  color: ${props => props.$color || '#2d5bed'};

  @media (max-width: 768px) {
    width: 42px;
    height: 42px;
    font-size: 20px;
    border-radius: 12px;
  }
`;

const OptionTextWrapper = styled.div`
  flex: 1;
  padding-top: 2px;
`;

const OptionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #192947;
  margin: 0 0 5px 0;
  line-height: 1.3;
`;

const OptionDescription = styled.p`
  font-size: 13px;
  color: #7b8aa3;
  margin: 0;
  line-height: 1.55;
`;

const OptionArrow = styled.span`
  color: #a9b5c8;
  font-size: 18px;
  align-self: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${OptionCard}:hover & {
    color: #7b8aa3;
    transform: translateX(3px);
  }
`;

// ============================================
// MEETING FLOW STYLED COMPONENTS (kept from original)
// ============================================

const SetMettingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid #edf1f9;
  width: 100%;
  margin: 0 auto;
  border-radius: 20px;
  max-height: none;
  overflow-y: visible;
  box-shadow: 0 8px 32px rgba(39, 68, 131, 0.07);

  @media (max-width: 768px) {
    width: 100%;
    max-height: none;
    padding: 20px;
    border-radius: 16px;
    margin: 0 auto;
    overflow-y: visible;
  }
`;

const ExpertImageWrapper = styled.div`
  width: 100%;
  height: auto;
  max-height: 200px;

  > img {
    border-radius: 10px;
    max-height: 100%;
    object-fit: cover;
  }
`;

const ExpertName = styled.div`
  font-size: 24px;
  font-weight: 300;
  line-height: 30.5px;
  text-align: left;
  margin-bottom: 4px;
  color: #192947;
`;

const ExpertRole = styled.div`
  font-size: 16px;
  line-height: 1.2;
  margin-bottom: 8px;
  color: #455776;
`;

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  align-self: center;
  margin-right: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px;

  img {
    width: 100%;
    height: 100%;
    opacity: 1;
  }
`;

// ============================================
// AI OVERVIEW PAYMENT STYLED COMPONENTS
// ============================================

const AiOverviewCard = styled.div`
  width: 100%;
  max-width: 600px;
  background: linear-gradient(160deg, #ffffff 0%, #f9fbff 50%, #f5f8fd 100%);
  border: 1px solid rgba(100, 150, 255, 0.12);
  border-radius: 20px;
  padding: 32px 32px;
  box-shadow: 0 24px 80px rgba(39, 68, 131, 0.14);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 24px 20px;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
    border-radius: 14px;
  }
`;

const AiOverviewTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #192947;
  margin: 0 0 6px 0;
  letter-spacing: -0.3px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const AiOverviewSubtitle = styled.p`
  font-size: 13px;
  color: #7b8aa3;
  line-height: 1.5;
  margin: 0 0 20px 0;
  text-align: center;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 12px;
    max-width: 100%;
    margin-bottom: 16px;
  }
`;

const AiOverviewFeatures = styled.div`
  background: rgba(136, 93, 232, 0.06);
  border: 1px solid rgba(136, 93, 232, 0.12);
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 14px 16px;
    margin-bottom: 16px;
    border-radius: 10px;
  }
`;

const AiOverviewIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(136, 93, 232, 0.25), rgba(136, 93, 232, 0.15));
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
  color: #7955cd;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    margin-bottom: 14px;
    border-radius: 12px;
  }
`;

const PromoInputRow = styled.div`
  display: flex;
  gap: 10px;
  max-width: 380px;
  margin: 0 auto 16px auto;

  @media (max-width: 768px) {
    max-width: 100%;
    gap: 8px;
  }
`;

const ActionButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  margin-top: 8px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const PrimaryActionBtn = styled.button`
  padding: 14px 36px;
  border-radius: 12px;
  background: ${props => props.$active
    ? 'linear-gradient(110deg, #885de8, #6d4fd6)'
    : 'rgba(136, 93, 232, 0.12)'};
  color: ${props => props.$active ? '#ffffff' : '#8f9db3'};
  border: none;
  cursor: ${props => props.$active && !props.disabled ? 'pointer' : 'default'};
  font-size: 15px;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active ? '0 4px 16px rgba(121, 85, 205, 0.3)' : 'none'};
  width: 100%;
  max-width: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    ${props => props.$active && !props.disabled && `
      transform: translateY(-1px);
      box-shadow: 0 6px 24px rgba(121, 85, 205, 0.4);
    `}
  }

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 14px;
    max-width: 100%;
  }
`;

const SecondaryActionBtn = styled.button`
  padding: 12px 32px;
  border-radius: 10px;
  background: #f8faff;
  color: #7b8aa3;
  border: 1px solid #edf1f9;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  width: 100%;
  max-width: 380px;

  &:hover {
    background: #f5f8fd;
    color: #455776;
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 13px;
    max-width: 100%;
  }
`;

// ============================================
// COMPONENT
// ============================================

const SetMetting = () => {
    console.log('SetMetting component is rendering');

    const authenticatedAxios = useAuthenticatedAxios();
    const { expertId, threadId } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const lang = queryParams.get('lang');
    const [mettingSet, setMetting] = useState(false);
    const [firstConnection, setFirstConnection] = useState({ state: 'loading' });
    const [expertData, setExpertData] = useState({});
    const [meetingSettings, setMeetingSettings] = useState({
        allowMeetingsFromWidget: true,
        allowManualMeetings: true,
        allowAiOverviewFromWidget: true
    });
    const [promoCode, setPromoCode] = useState('');
    const [promoStatus, setPromoStatus] = useState(null); // null | 'valid' | 'invalid'
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [analysisError, setAnalysisError] = useState(null);
    const navigate = useNavigate();

    // Check if this is the first time loading after auth
    const isFirstLoad = !sessionStorage.getItem(PROCESS_KEY);

    useEffect(() => {
        console.log('SetMeeting component mounted - processing data');

        const fetchData = async () => {
            try {
                const response = await authenticatedAxios.post(`/api/meetings/connectUserToConversation`, {
                    expertId,
                    threadId
                });

                if (response.data.flag) {
                    const expertInfo = response.data.info;
                    setExpertData(expertInfo);
                    
                    // Get meeting settings with defaults
                    const settings = expertInfo.meetingSettings || {
                        allowMeetingsFromWidget: true,
                        allowManualMeetings: true,
                        allowAiOverviewFromWidget: true
                    };
                    // Ensure allowAiOverviewFromWidget has a default
                    if (settings.allowAiOverviewFromWidget === undefined) {
                        settings.allowAiOverviewFromWidget = true;
                    }
                    setMeetingSettings(settings);

                    const showModal = settings.allowMeetingsFromWidget || settings.allowAiOverviewFromWidget;

                    if (!showModal) {
                        // Neither meetings nor AI overview allowed — redirect
                        console.log("No post-widget options enabled — redirecting");
                        setFirstConnection({ state: "noOptionsAvailable" });
                    } else {
                        // Show the "What's next?" modal
                        setFirstConnection({ state: "showChoices" });
                    }
                    
                    // Link funnel session to user if session exists in localStorage
                    linkFunnelSession();
                } else if (response.data.alreadyConnected && response.data.info) {
                    // User already connected — still show the choices modal (useful for simulate/testing)
                    const expertInfo = response.data.info;
                    setExpertData(expertInfo);
                    
                    const settings = expertInfo.meetingSettings || {
                        allowMeetingsFromWidget: true,
                        allowManualMeetings: true,
                        allowAiOverviewFromWidget: true
                    };
                    if (settings.allowAiOverviewFromWidget === undefined) {
                        settings.allowAiOverviewFromWidget = true;
                    }
                    setMeetingSettings(settings);

                    const showModal = settings.allowMeetingsFromWidget || settings.allowAiOverviewFromWidget;
                    if (!showModal) {
                        setFirstConnection({ state: "noOptionsAvailable" });
                    } else {
                        setFirstConnection({ state: "showChoices" });
                    }
                } else {
                    setFirstConnection({ state: "error" });
                }
            } catch (err) {
                console.log(err);
                // 409: this conversation was already claimed by a different account (the server no
                // longer hands it to whoever opens the link second).
                setFirstConnection({ state: err?.response?.status === 409 ? 'alreadyClaimed' : 'error' });
            } finally {
                // Mark that we've processed this setMeeting request
                sessionStorage.setItem(PROCESS_KEY, 'complete');
            }
        };

        const handleLanguageChange = async () => {
            try {
                const response = await authenticatedAxios.post(`api/settings/language`, {
                    language: lang || 'en'
                });
                console.log(response);
            } catch (err) {
                console.warn('Language setting not available:', err.message);
            }
        };
        
        // Link funnel session to user account
        const linkFunnelSession = async () => {
            try {
                const funnelSession = localStorage.getItem('imaven_funnel_session');
                if (funnelSession) {
                    const session = JSON.parse(funnelSession);
                    if (session.sessionId) {
                        console.log('[Funnel] Linking session to user via localStorage:', session.sessionId);
                        try {
                            await authenticatedAxios.put(`/api/funnel/session/${session.sessionId}/link`);
                            console.log('[Funnel] Session linked successfully');
                            return;
                        } catch (linkErr) {
                            // A stored session from another visit (404/409) — fall through to the
                            // session of THIS conversation instead of giving up.
                            console.log('[Funnel] Stored session not linkable, trying by conversation');
                        }
                    }
                }
                
                if (threadId) {
                    console.log('[Funnel] No localStorage session, trying to link by conversationId:', threadId);
                    try {
                        const sessionResponse = await authenticatedAxios.get(`/api/funnel/session/by-conversation/${threadId}`);
                        if (sessionResponse.data?.sessionId) {
                            console.log('[Funnel] Found session:', sessionResponse.data.sessionId);
                            await authenticatedAxios.put(`/api/funnel/session/${sessionResponse.data.sessionId}/link`);
                            console.log('[Funnel] Session linked successfully via conversationId');
                        }
                    } catch (lookupErr) {
                        console.log('[Funnel] No session found for conversationId (may not be a funnel lead)');
                    }
                }
            } catch (err) {
                console.warn('[Funnel] Failed to link session:', err);
            }
        };

        fetchData();
        handleLanguageChange();

        return () => {
            // Cleanup
        };
    }, [expertId, threadId, navigate, authenticatedAxios]);

    const finishSetMetting = () => {
        setMetting(true);
        setFirstConnection({ state: 'done' });
    }

    // ============================================
    // RENDER: Loading
    // ============================================
    if (firstConnection.state === 'loading') {
        return (
            <LoadingContainer>
                <LogoLoader size={180} message="Getting your data ready" />
            </LoadingContainer>
        );
    }

    // ============================================
    // RENDER: Error
    // ============================================
    if (firstConnection.state === 'alreadyClaimed') {
        return (
            <LoadingContainer>
                <p>This conversation is already linked to a different account.</p>
                <p>Sign in with the account you used before, or contact your adviser.</p>
            </LoadingContainer>
        );
    }

    if (firstConnection.state === 'error') {
        setTimeout(() => {
            navigate('/');
        }, 1000);

        return (
            <LoadingContainer>
                <p>Error loading data</p>
            </LoadingContainer>
        );
    }

    // ============================================
    // RENDER: Done (after meeting scheduled)
    // ============================================
    if (firstConnection.state === 'done') {
        setTimeout(() => {
            navigate('/');
        }, 1000);

        return (
            <LoadingContainer>
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(45, 91, 237, 0.2), rgba(41, 169, 221, 0.1))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px',
                    fontSize: '28px'
                }}>
                    &#10003;
                </div>
                <p style={{ fontSize: '20px', fontWeight: '600' }}>Meeting Scheduled!</p>
                <p style={{ fontSize: '14px', opacity: 0.6 }}>Redirecting to your dashboard...</p>
            </LoadingContainer>
        );
    }

    // ============================================
    // RENDER: No options available (both disabled) — redirect
    // ============================================
    if (firstConnection.state === 'noOptionsAvailable') {
        navigate(`/conversations/${threadId}?welcome=true`);
        
        return (
            <LoadingContainer>
                <LogoLoader size={180} message="Opening your conversation" />
            </LoadingContainer>
        );
    }

    // ============================================
    // RENDER: "What's next?" choices modal
    // ============================================
    if (firstConnection.state === 'showChoices') {
        return (
            <ModalOverlay>
                <ModalContent>
                    {/* Expert header */}
                    {expertData.expertName && (
                        <ModalExpertHeader>
                            {expertData.imgUrl && (
                                <ExpertAvatar>
                                    <img src={expertData.imgUrl} alt={expertData.expertName} />
                                </ExpertAvatar>
                            )}
                            <ExpertInfo>
                                <ExpertNameText>{expertData.expertName}</ExpertNameText>
                                <ExpertRoleText>{expertData.expertTilte || 'Expert Advisor'}</ExpertRoleText>
                            </ExpertInfo>
                        </ModalExpertHeader>
                    )}

                    {/* Title */}
                    <ModalTitle>What would you like to do next?</ModalTitle>
                    <ModalSubtitle>
                        Your conversation has been saved. Choose how you'd like to proceed with {expertData.expertName || 'your advisor'}.
                    </ModalSubtitle>

                    {/* Options */}
                    <OptionsContainer>
                        {/* Option 1: Set a meeting (only if allowed) */}
                        {meetingSettings.allowMeetingsFromWidget && (
                            <OptionCard onClick={() => setFirstConnection({ state: 'scheduleMeeting' })}>
                                <OptionIconWrapper $bg="rgba(45, 91, 237, 0.15)" $color="#2c64da">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" /></svg>
                                </OptionIconWrapper>
                                <OptionTextWrapper>
                                    <OptionTitle>Schedule a Meeting</OptionTitle>
                                    <OptionDescription>
                                        Book a personal consultation with {expertData.expertName || 'your advisor'}. Choose a convenient date and time for a one-on-one session.
                                    </OptionDescription>
                                </OptionTextWrapper>
                                <OptionArrow>&rarr;</OptionArrow>
                            </OptionCard>
                        )}

                        {/* Option 2: AI overview (only if allowed) */}
                        {meetingSettings.allowAiOverviewFromWidget && (
                            <OptionCard onClick={() => setFirstConnection({ state: 'aiOverview' })}>
                                <OptionIconWrapper $bg="rgba(136, 93, 232, 0.15)" $color="#7955cd">
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z"/><circle cx="9" cy="7" r="0.5" fill="currentColor"/><circle cx="15" cy="7" r="0.5" fill="currentColor"/></svg>
                                </OptionIconWrapper>
                                <OptionTextWrapper>
                                    <OptionTitle>Get an AI Case Overview</OptionTitle>
                                    <OptionDescription>
                                        Receive an initial analysis of your case from {expertData.expertName ? `${expertData.expertName}'s` : "our"} AI assistant. 
                                        Get a first understanding of which options may be right for you and what steps to consider.
                                    </OptionDescription>
                                </OptionTextWrapper>
                                <OptionArrow>&rarr;</OptionArrow>
                            </OptionCard>
                        )}

                        {/* Option 3: Continue to site (always) */}
                        <OptionCard onClick={() => navigate('/')}>
                            <OptionIconWrapper $bg="#f5f8fd" $color="#65748d">
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            </OptionIconWrapper>
                            <OptionTextWrapper>
                                <OptionTitle>Continue to Site</OptionTitle>
                                <OptionDescription>
                                    Go to your dashboard to browse conversations, explore services, or come back later.
                                </OptionDescription>
                            </OptionTextWrapper>
                            <OptionArrow>&rarr;</OptionArrow>
                        </OptionCard>
                    </OptionsContainer>
                </ModalContent>
            </ModalOverlay>
        );
    }

    // ============================================
    // RENDER: Schedule meeting flow (existing calendar)
    // ============================================
    if (firstConnection.state === 'scheduleMeeting') {
        return (
            <div style={{
                minHeight: 'calc(100vh - 80px)',
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                padding: '24px 16px',
                paddingBottom: '120px',
                overflowY: 'auto'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: '1100px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}>
                    {/* Header Section */}
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '8px'
                    }}>
                        <h1 style={{ 
                            fontSize: '28px', 
                            fontWeight: '600',
                            color: '#192947', 
                            marginBottom: '8px',
                            letterSpacing: '-0.5px'
                        }}>
                            Schedule Your Meeting
                        </h1>
                        <p style={{
                            fontSize: '15px',
                            color: '#65748d',
                            maxWidth: '400px',
                            margin: '0 auto',
                            lineHeight: '1.5'
                        }}>
                            Select a convenient date and time to meet with your advisor
                        </p>
                    </div>

                    {/* Calendar Section */}
                    <div style={{ width: '100%' }}>
                        {mettingSet ? (
                            <SetMettingContainer>
                                <div style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '40px 20px',
                                    textAlign: 'center'
                                }}>
                                    <div style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, rgba(45, 91, 237, 0.2), rgba(41, 169, 221, 0.1))',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '20px',
                                        fontSize: '28px'
                                    }}>
                                        &#10003;
                                    </div>
                                    <span style={{
                                        fontSize: '20px',
                                        fontWeight: '600',
                                        color: '#192947',
                                        marginBottom: '8px'
                                    }}>
                                        Meeting Scheduled!
                                    </span>
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#65748d'
                                    }}>
                                        Redirecting to your dashboard...
                                    </span>
                                </div>
                            </SetMettingContainer>
                        ) : (
                            <WeeklyCalendar
                                expertId={expertId}
                                isMettingScheduled={false}
                                expertName={expertData.expertName}
                                finishSettingMeeting={finishSetMetting}
                                threadId={threadId}
                            />
                        )}
                    </div>

                    {/* Info Cards Row */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '16px',
                        marginTop: '8px'
                    }}>
                        {/* Quick Info Card */}
                        <SetMettingContainer style={{ padding: '24px' }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '12px',
                                marginBottom: '16px'
                            }}>
                                <IconWrapper style={{ margin: 0, width: '32px', height: '32px' }}>
                                    <img src={CheckMarkIcon} alt="check mark" />
                                </IconWrapper>
                                <span style={{ fontSize: '16px', fontWeight: '600', color: '#192947' }}>
                                    What to Expect
                                </span>
                            </div>
                            <ul style={{
                                margin: 0,
                                paddingLeft: '20px',
                                color: '#455776',
                                fontSize: '14px',
                                lineHeight: '1.8'
                            }}>
                                <li>Your advisor will review your information before the meeting</li>
                                <li>You'll receive a confirmation email with meeting details</li>
                                <li>Manage appointments anytime from your dashboard</li>
                            </ul>
                        </SetMettingContainer>

                        {/* Adviser Info Card */}
                        <SetMettingContainer style={{ padding: '24px' }}>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'flex-start', 
                                gap: '16px' 
                            }}>
                                <ExpertImageWrapper style={{ 
                                    width: '80px', 
                                    height: '80px',
                                    flexShrink: 0,
                                    borderRadius: '12px',
                                    overflow: 'hidden'
                                }}>
                                    <img 
                                        src={expertData.imgUrl} 
                                        alt='advisor' 
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }} 
                                    />
                                </ExpertImageWrapper>
                                <div style={{ flex: 1 }}>
                                    <ExpertName style={{ fontSize: '18px', marginBottom: '4px' }}>
                                        {expertData.expertName}
                                    </ExpertName>
                                    <ExpertRole style={{ fontSize: '14px', marginBottom: '12px' }}>
                                        {expertData.expertTilte}
                                    </ExpertRole>
                                    <div style={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: '12px',
                                        fontSize: '13px',
                                        color: '#65748d'
                                    }}>
                                        {expertData.location && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                &#128205; {expertData.location}
                                            </span>
                                        )}
                                        {expertData.email && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                &#9993;&#65039; {expertData.email}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </SetMettingContainer>
                    </div>

                    {/* Back Button */}
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        marginTop: '8px',
                        paddingBottom: '20px'
                    }}>
                        <button
                            onClick={() => setFirstConnection({ state: 'showChoices' })}
                            style={{
                                padding: '12px 32px',
                                borderRadius: '10px',
                                background: '#f5f8fd',
                                color: '#455776',
                                border: '1px solid #edf1f9',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                transition: 'all 0.2s ease',
                                backdropFilter: 'blur(4px)'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#edf2ff';
                                e.currentTarget.style.color = '#192947';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#f5f8fd';
                                e.currentTarget.style.color = '#455776';
                            }}
                        >
                            &larr; Back to Options
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ============================================
    // HANDLER: Apply promo code
    // ============================================
    const handleApplyPromo = () => {
        if (promoCode.trim().toLowerCase() === 'imavenai') {
            setPromoStatus('valid');
        } else {
            setPromoStatus('invalid');
        }
    };

    // ============================================
    // HANDLER: Start analysis after payment/promo
    // ============================================
    const handleStartAnalysis = async () => {
        setAnalysisLoading(true);
        setAnalysisError(null);
        const base = process.env.REACT_APP_SERVER || '';
        console.info('[UserPlatform/Analysis] trigger POST', `${base}/api/analysis/trigger`, { threadId, expertId });
        try {
            // Trigger analysis via User_Platform_Server
            const res = await authenticatedAxios.post(`/api/analysis/trigger`, {
                threadId,
                expertId
            });
            console.info('[UserPlatform/Analysis] trigger OK', res.status, res.data?.message, res.data?.analysis?.success ?? res.data?.success);

            // Navigate to conversation with analysis flag
            navigate(`/conversations/${threadId}?aiAnalysis=started`);
        } catch (err) {
            const st = err.response?.status;
            const data = err.response?.data;
            console.error('[UserPlatform/Analysis] trigger FAILED', { status: st, data, message: err.message });
            setAnalysisError(
                data?.error || data?.details || (st ? `Server error (${st})` : 'Failed to start analysis. Please try again.')
            );
            setAnalysisLoading(false);
        }
    };

    // ============================================
    // RENDER: AI Overview — Payment / Promo Code
    // ============================================
    if (firstConnection.state === 'aiOverview') {
        return (
            <div style={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                width: '100%',
                padding: '24px 16px',
                overflowY: 'auto'
            }}>
                <AiOverviewCard>
                    {/* Top accent line */}
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                        background: 'linear-gradient(90deg, transparent, #7955cd, #885de8, transparent)'
                    }} />

                    {/* Icon */}
                    <AiOverviewIcon>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a4 4 0 0 1 4 4v2a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/><path d="M16 14H8a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4z"/><circle cx="9" cy="7" r="0.5" fill="currentColor"/><circle cx="15" cy="7" r="0.5" fill="currentColor"/></svg>
                    </AiOverviewIcon>

                    {/* Title */}
                    <AiOverviewTitle>Professional AI Case Analysis</AiOverviewTitle>

                    <AiOverviewSubtitle>
                        Powered by {expertData.expertName ? `${expertData.expertName}'s` : "our expert's"} professional knowledge base and specialized AI engine
                    </AiOverviewSubtitle>

                    {/* What you get */}
                    <AiOverviewFeatures>
                        <h4 style={{
                            fontSize: '12px', fontWeight: '600', color: '#7955cd',
                            margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '0.5px'
                        }}>
                            What you'll receive
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {[
                                { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>, text: 'Comprehensive case analysis based on your conversation' },
                                { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>, text: 'Personalized pathways tailored to your situation' },
                                { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/></svg>, text: 'Readiness assessment with score breakdown' },
                                { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/><path d="M9 14l2 2 4-4"/></svg>, text: 'Action items and recommended next steps' },
                                { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7955cd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>, text: 'Risks, blockers, and missing information' }
                            ].map((item, i) => (
                                <div key={i} style={{
                                    display: 'flex', alignItems: 'center', gap: '10px'
                                }}>
                                    <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
                                    <span style={{
                                        fontSize: '13px', color: '#65748d', lineHeight: '1.3'
                                    }}>
                                        {item.text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </AiOverviewFeatures>

                    {/* Price */}
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '8px', marginBottom: '16px'
                    }}>
                        <span style={{
                            fontSize: promoStatus === 'valid' ? '16px' : '24px',
                            fontWeight: '700',
                            color: promoStatus === 'valid' ? '#a9b5c8' : '#192947',
                            textDecoration: promoStatus === 'valid' ? 'line-through' : 'none',
                            transition: 'all 0.3s ease'
                        }}>
                            ${(meetingSettings.aiAnalysisPrice ?? 10).toFixed(2)}
                        </span>
                        {promoStatus === 'valid' && (
                            <span style={{
                                fontSize: '24px', fontWeight: '700',
                                color: '#0f8f63', marginLeft: '4px'
                            }}>
                                FREE
                            </span>
                        )}
                    </div>

                    {/* Promo code input */}
                    <PromoInputRow>
                        <input
                            type="text"
                            placeholder="Enter promo code"
                            value={promoCode}
                            onChange={(e) => {
                                setPromoCode(e.target.value);
                                if (promoStatus) setPromoStatus(null);
                            }}
                            disabled={promoStatus === 'valid'}
                            style={{
                                flex: 1, padding: '12px 16px', borderRadius: '10px',
                                background: promoStatus === 'valid'
                                    ? 'rgba(18, 165, 113, 0.08)'
                                    : '#f5f8fd',
                                border: promoStatus === 'valid'
                                    ? '1px solid rgba(18, 165, 113, 0.3)'
                                    : promoStatus === 'invalid'
                                        ? '1px solid rgba(229, 72, 77, 0.4)'
                                        : '1px solid #edf1f9',
                                color: '#192947', fontSize: '14px', outline: 'none',
                                transition: 'all 0.2s ease'
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                        />
                        <button
                            onClick={handleApplyPromo}
                            disabled={!promoCode.trim() || promoStatus === 'valid'}
                            style={{
                                padding: '12px 20px', borderRadius: '10px',
                                background: promoStatus === 'valid'
                                    ? 'rgba(18, 165, 113, 0.15)'
                                    : 'rgba(136, 93, 232, 0.2)',
                                color: promoStatus === 'valid' ? '#0f8f63' : '#7955cd',
                                border: 'none', cursor: promoCode.trim() && promoStatus !== 'valid' ? 'pointer' : 'default',
                                fontSize: '14px', fontWeight: '600',
                                opacity: !promoCode.trim() ? 0.5 : 1,
                                transition: 'all 0.2s ease',
                                flexShrink: 0
                            }}
                        >
                            {promoStatus === 'valid' ? '&#10003; Applied' : 'Apply'}
                        </button>
                    </PromoInputRow>

                    {/* Promo status message */}
                    {promoStatus === 'invalid' && (
                        <p style={{
                            fontSize: '13px', color: '#d63c3c', textAlign: 'center',
                            margin: '0 0 16px 0'
                        }}>
                            Invalid promo code. Please try again.
                        </p>
                    )}
                    {promoStatus === 'valid' && (
                        <p style={{
                            fontSize: '13px', color: '#0f8f63', textAlign: 'center',
                            margin: '0 0 16px 0'
                        }}>
                            Promo code applied successfully! Your analysis is free.
                        </p>
                    )}

                    {/* Error message */}
                    {analysisError && (
                        <p style={{
                            fontSize: '13px', color: '#d63c3c', textAlign: 'center',
                            margin: '0 0 16px 0'
                        }}>
                            {analysisError}
                        </p>
                    )}

                    {/* Action buttons */}
                    <ActionButtonsCol>
                        <PrimaryActionBtn
                            onClick={handleStartAnalysis}
                            disabled={promoStatus !== 'valid' || analysisLoading}
                            $active={promoStatus === 'valid'}
                        >
                            {analysisLoading ? (
                                <>
                                    <LogoLoader compact size={18} onDark label="Generating analysis" />
                                    Generating Analysis...
                                </>
                            ) : promoStatus === 'valid' ? (
                                'Start AI Analysis'
                            ) : (
                                'Enter promo code or pay to continue'
                            )}
                        </PrimaryActionBtn>

                        {promoStatus !== 'valid' && (
                            <div style={{ 
                                width: '100%', maxWidth: '380px',
                                display: 'flex', flexDirection: 'column', gap: '8px'
                            }}>
                                <div style={{ 
                                    fontSize: '12px', color: '#8f9db3', 
                                    textAlign: 'center', textTransform: 'uppercase', 
                                    letterSpacing: '0.5px', marginBottom: '4px'
                                }}>
                                    or pay with
                                </div>
                                <SecondaryActionBtn
                                    onClick={() => alert('PayPal payment integration coming soon.')}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                                    PayPal
                                </SecondaryActionBtn>
                                <SecondaryActionBtn
                                    onClick={() => alert('Stripe payment integration coming soon.')}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                                    Stripe
                                </SecondaryActionBtn>
                                <SecondaryActionBtn
                                    onClick={() => alert('Rapyd payment integration coming soon.')}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                                    Rapyd
                                </SecondaryActionBtn>
                            </div>
                        )}

                        <button
                            onClick={() => {
                                setPromoCode('');
                                setPromoStatus(null);
                                setAnalysisError(null);
                                setFirstConnection({ state: 'showChoices' });
                            }}
                            style={{
                                padding: '10px 24px', borderRadius: '10px',
                                background: 'transparent',
                                color: '#8f9db3',
                                border: 'none', cursor: 'pointer',
                                fontSize: '13px', fontWeight: '500',
                                transition: 'all 0.2s ease', marginTop: '4px'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.color = '#455776';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.color = '#8f9db3';
                            }}
                        >
                            &larr; Back to Options
                        </button>
                    </ActionButtonsCol>
                </AiOverviewCard>
            </div>
        );
    }

    return null;
};

export default SetMetting;
